/*
  VELUNE — premium header behaviour (no dependencies)

  1. Scroll state: sets data-vh-scrolled on #header-component once the page scrolls, so the CSS can fade the
     transparent header into the solid, compact one. Works with both scroll containers used by the theme
     (window on mobile, .page-wrapper on desktop).
  2. Mobile menu: builds the panel from the nav links, opens/closes it with a fade, locks the page scroll,
     traps focus, closes on Escape / link / resize.

  Styles live in velune-header.css.
*/
(function () {
  'use strict';

  var MOBILE = window.matchMedia('(max-width: 749px)');
  var root = document.documentElement;
  var lastFocus = null;

  function getHeader() {
    return document.getElementById('header-component');
  }

  /* ---------- 1. Scroll state ---------- */
  var scrolled = false;
  var ticking = false;

  function readScroll() {
    var wrapper = document.querySelector('.page-wrapper');
    return Math.max(
      window.scrollY || 0,
      (document.scrollingElement && document.scrollingElement.scrollTop) || 0,
      wrapper ? wrapper.scrollTop : 0
    );
  }

  function applyScroll() {
    ticking = false;
    var header = getHeader();
    if (!header) return;
    var top = readScroll();
    // Small hysteresis so the bar never flickers around the threshold.
    if (!scrolled && top > 12) scrolled = true;
    else if (scrolled && top < 4) scrolled = false;
    header.toggleAttribute('data-vh-scrolled', scrolled);
  }

  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(applyScroll);
  }

  // Capture phase: scroll events do not bubble, and the desktop scroller is .page-wrapper.
  document.addEventListener('scroll', onScroll, { passive: true, capture: true });
  window.addEventListener('pageshow', applyScroll);

  /* ---------- 2. Mobile menu ---------- */
  function getBurger() {
    return document.querySelector('#header-component .vh-burger');
  }

  function getPanel() {
    return document.querySelector('.vh-mobile');
  }

  function focusables(panel, burger) {
    var items = Array.prototype.slice.call(panel.querySelectorAll('a[href]'));
    return burger ? [burger].concat(items) : items;
  }

  function isOpen() {
    return root.classList.contains('vh-menu-open');
  }

  function openMenu() {
    var burger = getBurger();
    var panel = getPanel();
    if (!burger || !panel || isOpen()) return;
    lastFocus = document.activeElement;
    root.classList.add('vh-menu-open');
    panel.setAttribute('data-open', 'true');
    burger.setAttribute('aria-expanded', 'true');
    burger.setAttribute('aria-label', burger.getAttribute('data-label-close') || 'Fermer le menu');
    var first = panel.querySelector('a[href]');
    if (first) window.setTimeout(function () { first.focus({ preventScroll: true }); }, 60);
  }

  function closeMenu(restoreFocus) {
    var burger = getBurger();
    var panel = getPanel();
    if (!isOpen()) return;
    root.classList.remove('vh-menu-open');
    if (panel) panel.removeAttribute('data-open');
    if (burger) {
      burger.setAttribute('aria-expanded', 'false');
      burger.setAttribute('aria-label', burger.getAttribute('data-label-open') || 'Ouvrir le menu');
      if (restoreFocus !== false) burger.focus({ preventScroll: true });
    } else if (lastFocus && lastFocus.focus) {
      lastFocus.focus({ preventScroll: true });
    }
  }

  // Builds the panel links from the (hidden) mobile links of each nav item, and moves the panel to <body> so that
  // position: fixed covers the whole viewport (the header uses CSS containment).
  function build() {
    var burger = getBurger();
    var panel = document.querySelector('#header-component .vh-mobile') || getPanel();
    if (!burger || !panel) return;

    Array.prototype.forEach.call(document.querySelectorAll('body > .vh-mobile'), function (old) {
      if (old !== panel) old.remove();
    });

    var list = panel.querySelector('[data-vh-mobile-list]');
    if (list) {
      list.innerHTML = '';
      var sources = document.querySelectorAll('#header-component .velune-nav__mobile-link');
      Array.prototype.forEach.call(sources, function (source, index) {
        var li = document.createElement('li');
        var a = document.createElement('a');
        a.href = source.getAttribute('href') || '/';
        var title = document.createElement('span');
        title.className = 'vh-mobile__title';
        title.textContent = (source.textContent || '').trim();
        a.appendChild(title);
        var sub = source.getAttribute('data-sub');
        if (sub) {
          var subEl = document.createElement('span');
          subEl.className = 'vh-mobile__sub';
          subEl.textContent = sub;
          a.appendChild(subEl);
        }
        var arrow = document.createElement('span');
        arrow.className = 'vh-mobile__arrow';
        arrow.setAttribute('aria-hidden', 'true');
        arrow.textContent = '→';
        a.appendChild(arrow);
        li.style.setProperty('--i', String(index));
        a.style.setProperty('--i', String(index));
        li.appendChild(a);
        list.appendChild(li);
      });
    }

    if (panel.parentElement !== document.body) document.body.appendChild(panel);
    root.classList.add('vh-ready');
    root.classList.remove('vh-menu-open');
    panel.removeAttribute('data-open');
    burger.setAttribute('aria-expanded', 'false');
  }

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!(target instanceof Element)) return;
    var burger = target.closest('.vh-burger');
    if (burger) {
      if (isOpen()) closeMenu();
      else openMenu();
      return;
    }
    // Any link inside the panel closes it (same-page anchors included).
    if (isOpen() && target.closest('.vh-mobile a[href]')) closeMenu(false);
  });

  document.addEventListener('keydown', function (event) {
    if (!isOpen()) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      closeMenu();
      return;
    }
    if (event.key !== 'Tab') return;
    var panel = getPanel();
    if (!panel) return;
    var items = focusables(panel, getBurger());
    if (!items.length) return;
    // The panel lives at the end of <body>, so Tab order is managed by hand: burger → panel links → burger.
    var index = items.indexOf(document.activeElement);
    var next = event.shiftKey ? index - 1 : index + 1;
    if (index === -1) next = event.shiftKey ? items.length - 1 : 0;
    event.preventDefault();
    items[(next + items.length) % items.length].focus();
  });

  // Leaving the mobile layout (rotation, resize) closes the menu and releases the scroll lock.
  var onViewportChange = function () {
    if (!MOBILE.matches) closeMenu(false);
  };
  if (MOBILE.addEventListener) MOBILE.addEventListener('change', onViewportChange);
  else if (MOBILE.addListener) MOBILE.addListener(onViewportChange);

  function init() {
    build();
    applyScroll();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // Theme editor: the header section is re-rendered when settings change.
  document.addEventListener('shopify:section:load', init);
})();
